/**
 * WelcomeLogin.jsx — three-state welcome / link-up panel.
 *
 * Issue #305 — the previous implementation was hard-coded to the
 * "anonymous" state (urging Discord login regardless). EchoTiger and
 * other partial-state users (Discord OAuth complete but no ESO @-tag,
 * or a tag claimed via HMAC token but no Discord OAuth) saw a generic
 * "Login to unlock" nag that ignored what they had already linked.
 *
 * States detected from `wm.state.viewer` / `wm.state.identity?.tag`
 * plus the localStorage tag-claim sentinel:
 *   1. fully-linked  — Discord viewer + ESO tag → personalised Fissal
 *      greeting + Dashboards-rail pointer (issue #306).
 *   2. discord-only  — Discord viewer, no @-tag → urge tag claim.
 *   3. tag-only      — anonymous + claimed @-tag → urge Discord login.
 *   4. anonymous     — neither → original dual-CTA welcome.
 *
 * Compatible with the strict CSP (no inline handlers; uses Solid's
 * onClick / Show primitives).
 */
import { createSignal, createMemo, onMount, onCleanup, Show, For } from 'solid-js';
import wm from '../lib/wmStore.js';
import { launchClaimTag } from './ClaimTag.jsx';

// localStorage keys mirrored from ClaimTag.jsx (kept in sync — small enough
// to duplicate rather than introduce a new shared module).
const ANON_TAG_KEY = 'redfur-claimed-tag';

function readClaimedTag() {
  try { return localStorage.getItem(ANON_TAG_KEY) || ''; } catch { return ''; }
}

// Build a same-origin returnTo from the current URL so a deep-link
// (#307) survives the OAuth round-trip when the user clicks Login from
// the welcome card. Falls back to /v2/ when window is unavailable
// (vitest / SSR contexts) or the path is suspicious.
function loginHref(persona = null) {
  try {
    const path = window.location.pathname || '/v2/';
    const search = window.location.search || '';
    const target = path + search;
    const base = (!target.startsWith('/') || target.startsWith('//'))
      ? '/auth/login?returnTo=/v2/'
      : '/auth/login?returnTo=' + encodeURIComponent(target);
    return persona ? `${base}&persona=${encodeURIComponent(persona)}` : base;
  } catch {
    return persona ? `/auth/login?persona=${encodeURIComponent(persona)}&returnTo=/v2/` : '/auth/login?returnTo=/v2/';
  }
}

function logoutHref() {
  try {
    const path = window.location.pathname || '/v2/';
    const search = window.location.search || '';
    const target = path + search;
    const returnTo = (!target.startsWith('/') || target.startsWith('//')) ? '/v2/' : target;
    return '/auth/logout?returnTo=' + encodeURIComponent(returnTo);
  } catch {
    return '/auth/logout?returnTo=/v2/';
  }
}

const containerStyle = {
  display: 'grid',
  gap: 'var(--sp-2)',
  'text-align': 'left',
  padding: 'var(--sp-2) var(--sp-2) var(--sp-3)',
  'max-width': '58ch',
};
const titleStyle = {
  margin: 0,
  'font-family': 'var(--font-terminal)',
  'font-size': 'var(--fs-xl)',
  color: 'var(--fissal)',
  display: 'flex',
  'align-items': 'center',
  gap: '8px',
};
const bodyStyle = {
  margin: 0,
  color: 'var(--t-green-dim)',
  'line-height': '1.45',
};
const actionsStyle = {
  display: 'grid',
  gap: 'var(--sp-2)',
  'justify-items': 'start',
};
const ctaRowStyle = {
  display: 'flex',
  'flex-wrap': 'wrap',
  gap: '8px',
  'align-items': 'center',
};
const primaryBtnStyle = {
  display: 'inline-flex',
  'align-items': 'center',
  'justify-content': 'center',
  padding: '7px 14px',
  'border-radius': 'var(--r-md)',
  border: '1px solid var(--fissal)',
  color: 'var(--fissal)',
  'box-shadow': 'var(--glow-sm)',
  'text-decoration': 'none',
  'font-family': 'var(--font-mono)',
  'font-size': 'var(--fs-sm)',
  background: 'color-mix(in srgb, var(--fissal) 10%, transparent)',
  cursor: 'pointer',
};
const secondaryBtnStyle = {
  display: 'inline-flex',
  'align-items': 'center',
  'justify-content': 'center',
  padding: '7px 13px',
  'border-radius': 'var(--r-md)',
  border: '1px solid var(--window-edge, var(--t-green-dim))',
  background: 'rgba(0, 0, 0, 0.22)',
  color: 'var(--t-white)',
  'font-family': 'var(--font-mono)',
  'font-size': 'var(--fs-sm)',
  cursor: 'pointer',
};
const featureStripStyle = {
  display: 'flex',
  'flex-wrap': 'wrap',
  gap: '6px',
  'margin-top': '2px',
};
const featureBadgeStyle = {
  display: 'inline-flex',
  'align-items': 'center',
  gap: '4px',
  padding: '2px 8px',
  'border-radius': '999px',
  border: '1px solid color-mix(in srgb, var(--window-edge) 75%, transparent)',
  background: 'rgba(0, 0, 0, 0.24)',
  color: 'var(--t-green)',
  'font-family': 'var(--font-mono)',
  'font-size': '10px',
};
const devSwitchStyle = {
  display: 'flex',
  'flex-wrap': 'wrap',
  gap: 'var(--sp-2)',
  'margin-top': 'var(--sp-1)',
};
const devChipStyle = {
  display: 'inline-flex',
  'align-items': 'center',
  'justify-content': 'center',
  padding: '4px 10px',
  'border-radius': '999px',
  border: '1px solid var(--fissal)',
  color: 'var(--fissal)',
  'text-decoration': 'none',
  'font-size': 'var(--fs-xs)',
  'font-family': 'var(--font-mono)',
  background: 'rgba(0, 255, 136, 0.08)',
};

export default function WelcomeLogin() {
  const viewer = () => wm.state.viewer;
  const tag = () => wm.state.identity?.tag || null;
  const testingGround = () => wm.state.testingGround?.enabled ? wm.state.testingGround : null;
  const [claimed, setClaimed] = createSignal(readClaimedTag());
  const savedDashCount = createMemo(() => (wm.state.dashboards || []).filter(d => d && d.id !== 'anon').length);

  // Listen for tag-claim updates from the ClaimTag widget (both cross-tab
  // storage events and same-tab custom events) so the welcome card flips
  // state immediately without a reload.
  function onStorage(e) {
    if (!e || e.key === ANON_TAG_KEY || e.key === null) {
      setClaimed(readClaimedTag());
    }
  }
  function onClaimedTagEvent() {
    setClaimed(readClaimedTag());
  }
  onMount(() => {
    window.addEventListener('storage', onStorage);
    window.addEventListener('redfur:claimed-tag-change', onClaimedTagEvent);
  });
  onCleanup(() => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('redfur:claimed-tag-change', onClaimedTagEvent);
  });

  function onClaim() { launchClaimTag(); }

  const fullyLinked = () => !!viewer() && !!tag();
  const discordOnly = () => !!viewer() && !tag();
  const tagOnly     = () => !viewer() && !!claimed();
  const anonymous   = () => !viewer() && !claimed();

  const currentRoleLabel = () => testingGround()?.personas?.find(p => p.slug === testingGround()?.currentPersona)?.label || (viewer() ? 'Signed in' : 'Anonymous');

  return (
    <div class="v2-widget">
      <div class="v2-widget__scroll">
        <div style={containerStyle}>
          <div style={titleStyle}>Redfur Trading Terminal</div>

          <Show when={testingGround()}>
            <div style={{ 'margin-bottom': 'var(--sp-1)', color: 'var(--t-green-dim)', 'font-size': 'var(--fs-sm)' }}>
              Testing ground: <strong>{currentRoleLabel()}</strong>
            </div>
            <div style={devSwitchStyle}>
              <For each={testingGround()?.personas || []}>{(persona) => (
                <a href={loginHref(persona.slug)} style={devChipStyle}>{persona.label}</a>
              )}</For>
              <a href={logoutHref()} style={devChipStyle}>Logout</a>
            </div>
          </Show>

          {/* ── Fully linked — personalised Fissal greeting + dashboards pointer (#306) ── */}
          <Show when={fullyLinked()}>
            <p style={bodyStyle}>
              Welcome back, <strong>{String(tag() || viewer()?.displayName || 'trader').replace(/^@+/, '@')}</strong>.
              {' '}<em>*ear flick*</em> — Jone and Jode hold your frequency steady. Your account is fully linked.
            </p>
            <p style={{ ...bodyStyle, 'margin-top': 'var(--sp-1)' }}>
              Slip into a dashboard layout when you're ready: open the{' '}
              <strong>Home (☰) → Dashboards</strong> menu or click any desktop shortcut icon.
            </p>
            <Show when={savedDashCount() > 1}>
              <p style={{ ...bodyStyle, 'font-size': 'var(--fs-xs)', 'margin-top': 'var(--sp-1)' }}>
                You have {savedDashCount()} dashboard layouts saved.
              </p>
            </Show>
            <p style={{ ...bodyStyle, 'font-size': 'var(--fs-xs)', 'margin-top': 'var(--sp-1)' }}>
              You can close this panel — it won't reappear once your account
              is fully linked.
            </p>
          </Show>

          {/* ── Discord-only — urge the missing @-tag link ───────────── */}
          <Show when={discordOnly()}>
            <div style={actionsStyle}>
              <p style={bodyStyle}>
                Welcome{viewer()?.displayName ? ', ' + viewer().displayName : ''}!
                Link your ESO @-tag so guild sales, sync points, and your
                personal history can attach to your account.
              </p>
              <button type="button" onClick={onClaim} style={primaryBtnStyle}>
                🏷️ Link your ESO @-tag
              </button>
            </div>
          </Show>

          {/* ── Tag-only — urge Discord login to upgrade the partial state ── */}
          <Show when={tagOnly()}>
            <div style={actionsStyle}>
              <p style={bodyStyle}>
                @-tag linked on this device
                {claimed() ? ' (' + String(claimed()).replace(/^@+/, '@') + ')' : ''}.
                Log in with Discord to unlock Fissal AI, cross-device sync,
                and your personal sync points.
              </p>
              <a href={loginHref()} style={primaryBtnStyle}>Login with Discord</a>
            </div>
          </Show>

          {/* ── Anonymous — dual CTA + desktop showcase guide ────────── */}
          <Show when={anonymous()}>
            <div style={actionsStyle}>
              <p style={bodyStyle}>
                Explore live Tamriel market intelligence, floating Masser &amp; Secunda
                lunar phases, and guild leaderboards — or sign in to unlock your
                personal sales, sync points, and Fissal AI.
              </p>
              <div style={ctaRowStyle}>
                <a href={loginHref()} style={primaryBtnStyle}>🔑 Login with Discord</a>
                <button type="button" onClick={onClaim} style={secondaryBtnStyle}>
                  🏷️ Claim by ESO @-tag
                </button>
              </div>
              <div style={featureStripStyle}>
                <span style={featureBadgeStyle} title="Pin any window's active filters onto your desktop with 📌">📌 Pin window filters to desktop</span>
                <span style={featureBadgeStyle} title="Drag modules from ☰ Start onto the desktop">☰ Drag widgets from Start</span>
                <span style={featureBadgeStyle} title="Switch ESO province themes from ☰ → Theme">🎨 12 ESO Themes</span>
              </div>
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
}
